import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Box, ExclamationSolid, Flex, rem, Text, VStack } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import type { ProgramVisibilityFeatureId } from './feature-access/program-visibility-feature-toggle-group';

const FEATURE_ACCESS_PATH = '/admin/company/settings/feature-access';

type ProgramFeatureHiddenBannerProps = {
	featureId: ProgramVisibilityFeatureId;
};

export const ProgramFeatureHiddenBanner: FC<ProgramFeatureHiddenBannerProps> = ({ featureId }) => {
	const { t } = useTranslation('admin', { keyPrefix: 'company.customization.program_feature_hidden_banner' });
	const company = useCurrentCompany();
	const enabled = company?.feature_states?.[featureId] !== false;

	if (enabled) {
		return null;
	}

	return (
		<Box px={10} pt={8} mb={4} data-testid={`program-feature-hidden-banner-${featureId}`}>
			<Flex alignItems="flex-start" bgColor="warning.bg" p={rem(12)} borderRadius="md">
				<ExclamationSolid aria-hidden color="warning.icon" size="md" mr={rem(8)} flexShrink={0} />
				<VStack alignItems="flex-start" spacing={0} flex={1}>
					<Text color="text.soft" fontSize={100} fontWeight="semibold" lineHeight={6} letterSpacing="ui">
						{t(`${featureId}_title`)}
					</Text>
					<Text color="text.default" fontSize={100} fontWeight="normal" lineHeight={6} letterSpacing="ui">
						<Trans
							ns="admin"
							i18nKey={`company.customization.program_feature_hidden_banner.${featureId}_body`}
							components={[
								<Text
									key="feature-access-link"
									as={RouterLink}
									to={FEATURE_ACCESS_PATH}
									color="text.interactive"
									textDecoration="none"
									display="inline"
									fontSize="inherit"
									fontWeight="medium"
								/>,
							]}
						/>
					</Text>
				</VStack>
			</Flex>
		</Box>
	);
};
